// =====================================================================================
// INVRT Radar — web console — "Push to a target"
//
// POST /contacts/send with channel 'target' (docs/API-CONTRACTS-C1.md §5): one pending
// delivery attempt and one delivery.push job per contact, for an ACTIVE target of this
// organisation. The server refuses a paused or disabled target (target_not_found), so the
// list here is already narrowed to active ones, and an organisation with none is pointed
// at Account → Delivery rather than shown an empty select.
// =====================================================================================

import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSession } from "@/auth/SessionProvider";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Select } from "@/components/ui/input";
import { EmptyState, ErrorState, LoadingState } from "@/components/States";
import { pluralise } from "@/lib/format";
import type { DeliveryTarget, DeliveryTargetKind } from "@/lib/types.c1";
import { MAX_SEND_IDS, useDeliveryTargets, useSendContacts } from "./useContacts";

const KIND_LABEL: Record<DeliveryTargetKind, string> = {
  email: "Email",
  hubspot: "HubSpot",
  pipedrive: "Pipedrive",
  invrt_crm: "INVRT CRM",
  monti: "Monti",
};

export interface PushToTargetDialogProps {
  open: boolean;
  contactIds: string[];
  onClose: () => void;
  /** Called after the server has queued the push (the caller clears its selection). */
  onPushed?: () => void;
}

/** Mounted only whilst open, so each opening re-reads the targets and starts a fresh mutation. */
export function PushToTargetDialog(props: PushToTargetDialogProps) {
  if (!props.open) return null;
  return <PushToTargetForm {...props} />;
}

function PushToTargetForm({ contactIds, onClose, onPushed }: PushToTargetDialogProps) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { orgId } = useSession();
  const targets = useDeliveryTargets();
  const push = useSendContacts();
  const formId = useId();
  const [targetId, setTargetId] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);

  const active: DeliveryTarget[] = (targets.data?.targets ?? []).filter(
    (target) => target.status === "active",
  );
  const tooMany = contactIds.length > MAX_SEND_IDS;

  function submit(): void {
    const target = active.find((candidate) => candidate.id === targetId);
    if (!target) {
      setFieldError("Choose a delivery target.");
      return;
    }
    setFieldError(null);

    push.mutate(
      { contact_ids: contactIds, channel: "target", target_id: target.id },
      {
        onSuccess: (response) => {
          toast.push({
            tone: "success",
            title: `Queued for ${target.label}`,
            body: `${pluralise(response.count, "contact")} queued for delivery. Progress is under Account, Delivery.`,
          });
          // Attempts now exist for this target; any list of them is stale.
          void queryClient.invalidateQueries({ queryKey: ["delivery-targets", orgId] });
          onPushed?.();
          onClose();
        },
      },
    );
  }

  const body = (() => {
    if (targets.isLoading) return <LoadingState label="Loading delivery targets…" />;
    if (targets.isError) {
      return <ErrorState error={targets.error} onRetry={() => void targets.refetch()} />;
    }
    if (active.length === 0) {
      return (
        <EmptyState
          title="No active delivery targets"
          body="Set up where contacts should go, such as HubSpot, Pipedrive or an email inbox, then push from here."
          action={
            <Link
              to="/account?tab=delivery"
              className="text-sm font-medium text-primary underline-offset-2 hover:underline"
            >
              Set up a delivery target
            </Link>
          }
        />
      );
    }
    return (
      <form
        id={formId}
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Select
          label="Delivery target"
          value={targetId}
          onChange={(event) => setTargetId(event.target.value)}
          hint="Only active targets are listed. Paused targets hold contacts until they are resumed."
          {...(fieldError ? { error: fieldError } : {})}
        >
          <option value="">Choose a target</option>
          {active.map((target) => (
            <option key={target.id} value={target.id}>
              {target.label} ({KIND_LABEL[target.kind]})
            </option>
          ))}
        </Select>
        {tooMany ? (
          <p className="text-sm text-danger">
            Up to {MAX_SEND_IDS} contacts can be pushed in one go. Narrow the selection and try again.
          </p>
        ) : null}
        {push.isError ? <ErrorState error={push.error} /> : null}
      </form>
    );
  })();

  return (
    <Dialog
      open
      onClose={onClose}
      title="Push to a delivery target"
      description={`${pluralise(contactIds.length, "contact")} will be queued for delivery exactly as a new arrival would be. Pushing costs nothing.`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={push.isPending}>
            Cancel
          </Button>
          {active.length > 0 ? (
            <Button type="submit" form={formId} loading={push.isPending} disabled={tooMany}>
              Push
            </Button>
          ) : null}
        </>
      }
    >
      {body}
    </Dialog>
  );
}
